import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CreditCard as CreditCardIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  fetchPackages,
  createPackage,
  updatePackage,
  deletePackage
} from '../../redux/slices/creditPackageSlice';
import { useHasPermission } from '../../hooks/usePermission';
import ForbiddenPage from '../ForbiddenPage';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import alertify from 'alertifyjs';

/**
 * CreditPackagesPage - Admin credit packages management page
 * Phase 7: Credit System
 * 
 * Features:
 * - Package list with search
 * - Create, Edit, Delete (soft delete) actions
 * - Active/Inactive toggle
 * - Order management
 * - Pagination
 * 
 * Permission: credit-packages.manage
 * ⚠️ CRITICAL: MUI ONLY (NO TailwindCSS)
 */
const CreditPackagesPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { packages, loading, error } = useSelector(state => state.creditPackages);

  const hasPermission = useHasPermission('credit-packages.manage');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    credits: '',
    price: '',
    bonusCredits: '',
    order: '',
    description: '',
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({});

  // Debounce search term (500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch packages
  useEffect(() => {
    if (hasPermission) {
      dispatch(fetchPackages());
    }
  }, [dispatch, hasPermission]);

  const handleCreateClick = () => {
    setFormData({
      name: '',
      credits: '',
      price: '',
      bonusCredits: '',
      order: '',
      description: '',
      isActive: true
    });
    setFormErrors({});
    setCreateModalOpen(true);
  };

  const handleEditClick = (pkg) => {
    setSelectedPackage(pkg);
    setFormData({
      name: pkg.name || '',
      credits: pkg.credits || '',
      price: pkg.price || '',
      bonusCredits: pkg.bonusCredits || '',
      order: pkg.order || '',
      description: pkg.description || '',
      isActive: pkg.isActive !== undefined ? pkg.isActive : true
    });
    setFormErrors({});
    setEditModalOpen(true);
  };

  const handleDeleteClick = (pkg) => {
    setSelectedPackage(pkg);
    setDeleteDialogOpen(true);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Paket adı gereklidir';
    }

    if (!formData.credits || parseFloat(formData.credits) < 1) {
      errors.credits = 'Kredi miktarı en az 1 olmalıdır';
    }

    if (!formData.price || parseFloat(formData.price) < 0) {
      errors.price = 'Fiyat 0 veya daha büyük olmalıdır';
    }

    if (formData.bonusCredits && parseFloat(formData.bonusCredits) < 0) {
      errors.bonusCredits = 'Bonus kredi negatif olamaz';
    }

    if (formData.order && parseFloat(formData.order) < 0) {
      errors.order = 'Sıra negatif olamaz';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await dispatch(createPackage({
        name: formData.name.trim(),
        credits: parseInt(formData.credits, 10),
        price: parseFloat(formData.price),
        bonusCredits: formData.bonusCredits ? parseInt(formData.bonusCredits, 10) : 0,
        order: formData.order ? parseInt(formData.order, 10) : 0,
        description: formData.description.trim() || null
      })).unwrap();

      alertify.success('Kredi paketi başarıyla oluşturuldu');
      setCreateModalOpen(false);
      dispatch(fetchPackages());
    } catch (err) {
      alertify.error(err.message || 'Kredi paketi oluşturulamadı');
    }
  };

  const handleUpdate = async () => {
    if (!validateForm() || !selectedPackage) {
      return;
    }

    try {
      await dispatch(updatePackage({
        packageId: selectedPackage._id,
        data: {
          name: formData.name.trim(),
          credits: parseInt(formData.credits, 10),
          price: parseFloat(formData.price),
          bonusCredits: formData.bonusCredits ? parseInt(formData.bonusCredits, 10) : 0,
          order: formData.order ? parseInt(formData.order, 10) : 0,
          description: formData.description.trim() || null,
          isActive: formData.isActive
        }
      })).unwrap();

      alertify.success('Kredi paketi başarıyla güncellendi');
      setEditModalOpen(false);
      setSelectedPackage(null);
      dispatch(fetchPackages());
    } catch (err) {
      alertify.error(err.message || 'Kredi paketi güncellenemedi');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPackage) return;

    try {
      await dispatch(deletePackage(selectedPackage._id)).unwrap();
      alertify.success('Kredi paketi başarıyla pasifleştirildi');
      setDeleteDialogOpen(false);
      setSelectedPackage(null);
      dispatch(fetchPackages());
    } catch (err) {
      alertify.error(err.message || 'Kredi paketi pasifleştirilemedi');
    }
  };

  const handleToggleActive = async (pkg) => {
    try {
      await dispatch(updatePackage({
        packageId: pkg._id,
        data: { isActive: !pkg.isActive }
      })).unwrap();
      alertify.success(`Paket ${!pkg.isActive ? 'aktifleştirildi' : 'pasifleştirildi'}`);
      dispatch(fetchPackages());
    } catch (err) {
      alertify.error(err.message || 'Paket durumu güncellenemedi');
    }
  };

  const handleFormChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Filter packages by search term
  const filteredPackages = packages.filter(pkg => {
    if (!debouncedSearch) return true;
    const search = debouncedSearch.toLowerCase();
    return (
      pkg.name?.toLowerCase().includes(search) ||
      pkg.description?.toLowerCase().includes(search)
    );
  });

  if (!hasPermission) {
    return <ForbiddenPage />;
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => history.goBack()}>
              <ArrowBackIcon />
            </IconButton>
            <CreditCardIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Kredi Paketleri Yönetimi
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Kredi paketlerini oluşturun, düzenleyin ve yönetin
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
          >
            Yeni Paket Oluştur
          </Button>
        </Box>

        {/* Search */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <TextField
            placeholder="Paket adı veya açıklama ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Ad</strong></TableCell>
                <TableCell><strong>Kredi</strong></TableCell>
                <TableCell><strong>Fiyat</strong></TableCell>
                <TableCell><strong>Bonus</strong></TableCell>
                <TableCell><strong>Sıra</strong></TableCell>
                <TableCell><strong>Durum</strong></TableCell>
                <TableCell><strong>Oluşturulma</strong></TableCell>
                <TableCell align="right"><strong>İşlemler</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredPackages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {debouncedSearch ? 'Paket bulunamadı' : 'Henüz paket oluşturulmamış'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPackages.map((pkg) => {
                  return (
                    <TableRow key={pkg._id} hover>
                      <TableCell>{pkg.name}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {pkg.credits} Kredi
                        </Typography>
                        {pkg.bonusCredits > 0 && (
                          <Typography variant="caption" color="success.main">
                            +{pkg.bonusCredits} Bonus
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{pkg.price.toFixed(2)} TL</TableCell>
                      <TableCell>{pkg.bonusCredits || 0}</TableCell>
                      <TableCell>{pkg.order || 0}</TableCell>
                      <TableCell>
                        <Chip
                          label={pkg.isActive ? 'Aktif' : 'Pasif'}
                          color={pkg.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {pkg.createdAt
                          ? format(new Date(pkg.createdAt), 'dd MMM yyyy HH:mm', { locale: tr })
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Tooltip title={pkg.isActive ? 'Pasifleştir' : 'Aktifleştir'}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleActive(pkg)}
                            >
                              <Switch
                                checked={pkg.isActive}
                                size="small"
                                disabled
                                sx={{ pointerEvents: 'none' }}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Düzenle">
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(pkg)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Pasifleştir">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(pkg)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create Modal */}
        <Dialog
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Yeni Kredi Paketi</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Paket Adı"
                  value={formData.name}
                  onChange={handleFormChange('name')}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Kredi Miktarı"
                  type="number"
                  value={formData.credits}
                  onChange={handleFormChange('credits')}
                  error={!!formErrors.credits}
                  helperText={formErrors.credits}
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Fiyat (TL)"
                  type="number"
                  value={formData.price}
                  onChange={handleFormChange('price')}
                  error={!!formErrors.price}
                  helperText={formErrors.price}
                  required
                  inputProps={{ min: 0, step: '0.01' }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Bonus Kredi"
                  type="number"
                  value={formData.bonusCredits}
                  onChange={handleFormChange('bonusCredits')}
                  error={!!formErrors.bonusCredits}
                  helperText={formErrors.bonusCredits}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Sıra"
                  type="number"
                  value={formData.order}
                  onChange={handleFormChange('order')}
                  error={!!formErrors.order}
                  helperText={formErrors.order}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Açıklama"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={handleFormChange('description')}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateModalOpen(false)}>İptal</Button>
            <Button onClick={handleCreate} variant="contained">Oluştur</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Modal */}
        <Dialog
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Kredi Paketini Düzenle</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Paket Adı"
                  value={formData.name}
                  onChange={handleFormChange('name')}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Kredi Miktarı"
                  type="number"
                  value={formData.credits}
                  onChange={handleFormChange('credits')}
                  error={!!formErrors.credits}
                  helperText={formErrors.credits}
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Fiyat (TL)"
                  type="number"
                  value={formData.price}
                  onChange={handleFormChange('price')}
                  error={!!formErrors.price}
                  helperText={formErrors.price}
                  required
                  inputProps={{ min: 0, step: '0.01' }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Bonus Kredi"
                  type="number"
                  value={formData.bonusCredits}
                  onChange={handleFormChange('bonusCredits')}
                  error={!!formErrors.bonusCredits}
                  helperText={formErrors.bonusCredits}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Sıra"
                  type="number"
                  value={formData.order}
                  onChange={handleFormChange('order')}
                  error={!!formErrors.order}
                  helperText={formErrors.order}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Açıklama"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={handleFormChange('description')}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleFormChange('isActive')}
                      color="primary"
                    />
                  }
                  label="Aktif"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditModalOpen(false)}>İptal</Button>
            <Button onClick={handleUpdate} variant="contained">Kaydet</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Paketi Pasifleştir"
          message={`"${selectedPackage?.name}" başlıklı kredi paketini pasifleştirmek istediğinizden emin misiniz? Pasifleştirilen paketler kredi satın alma sayfasında görünmeyecektir.`}
          confirmText="Pasifleştir"
          cancelText="İptal"
          severity="warning"
        />
      </Box>
    </Container>
  );
};

export default CreditPackagesPage;

